<template>
	<div class="intro">

		<div class="intro-outer">

			<div class="typo-wrapper">
				<div id="headline-wrapper">
					<span class="typo-large">CURRICLE</span>
					<span class="typo-large">LENS</span>
				</div>
				<span class="typo-small">Visualized, annotated, <br/> connected</span>
				<span class="typo-by">A project of <a class="link" href="https://metalabharvard.github.io/">metalab (at) Harvard</a><br> and part of <a class="link" href="https://curricle.berkman.harvard.edu/">Curricle research</a></span>
			</div>

			<div class="intro-canvas">
				<div class="text-wrap">
					<span class="subline">
						Curricle Lens uses data narratives to explore the historical nature of the college curriculum, leveraging data and design in a post-disciplinary era.
					</span>
				</div>

				<div class="text-wrap" style="margin-bottom: 20px;">
					<span style="font-weight: 600; margin-bottom: 10px;">Data Narratives:</span><br/>
					<span style="font-style: normal; opacity: .5;" class="subline">
						Select a story on teaching and learning over time
					</span>
				</div>


				<div @click="dispatchCurrentStory(index)" class="story-outer" v-for="(story,index) in storiesArr" :key="story.id">
					<div class="story-title">
						<span>{{ story.introduction.title }}</span>

						<svg width="19px" height="14px" viewBox="0 0 19 14" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
							<g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
								<g id="Artboard" transform="translate(-222.000000, -120.000000)" stroke="#000000" stroke-width="2.4">
									<g id="Group-4" transform="translate(224.000000, 121.000000)">
										<path d="M14,6 L0,6" id="Line-2" stroke-linecap="square"></path>
										<polyline id="Path-2-Copy" transform="translate(12.000000, 6.000000) rotate(-90.000000) translate(-12.000000, -6.000000) " points="6 3 12.1266763 9 18 3"></polyline>
									</g>
								</g>
							</g>
						</svg>

					</div>
					<span class="subtitle">{{ introContent[index] }}</span>
				</div>

				<div class="text-wrap">
					<p style="opacity: .5;">
						A research project of <a class="link" href="https://metalabharvard.github.io/">metaLAB (at) Harvard</a>, <b>Curricle Lens</b> uses data narration to explore the conversational nature of the college curriculum. With comprehensive data on more than three decades of course offerings, we're able to detect historical trends. Shifts in course titles and formats allow us to trace the signals of topics, issues, and meanings. And by collating these data shifts with historical events, we can gauge the pulse of external events—social movements; musical genres; literary publications—that shape and redirect the ordered progress of college teaching and learning. Our data include the contents of online Harvard course catalogues from 1990 to the present, archived by the office of the Registrar, with data from earlier decades we've developed from archival sources. <b>Curricle Lens</b> offers a series of data stories that visualize discrete patterns in these data over time, enfolded with and enlivened by essays that ground and contextualize the data with archival material, storytelling, and argument. These stories are provocations to further exploration. The curriculum takes surprising turns throughout its journey—and we haven't found all the twists yet. <b>Curricle Lens</b> is an invitation: to join us in exploring these and other stories of teaching and learning, at Harvard and beyond.
					</p>

					<p style="opacity: .5;">
						<i>Designed, developed, and narrated by <a class="link" href="https://kimalbrecht.com/">Kim Albrecht</a>, <a class="link" href="https://metalabharvard.github.io/people/matthew">Matthew Battles</a>, <a class="link" href="https://www.fabiandinklage.com/">Fabian Dinklage</a>, and <a class="link" href="https://metalabharvard.github.io/about/">Sydney Lewis</a></i>
					</p>
				</div>

			</div>

			<!-- <div class="scroll-wrapper">
				<span class="arrow">↓</span>
				<span class="text">Scroll overlay to continue</span>
			</div> -->

		</div>

		<LineStackingIntro
			:sidebarVisible="true"
			:content="content"
			:storyActive="0"
			dataset="./data/story-two-cultures/reduced/CLASSICS_THE_1990_2020_.json"
			:snippet="content.stories[0].snippets[0]"
			:statements="this.statements"
		/>

		<Share :content="content.social"/>



	</div>
</template>

<script>
	import Share from './Share.vue';
	import LineStackingIntro from './LineStackingIntro.vue';
	import { select as d3Select } from 'd3';


	export default {
		name: 'Intro',
		props: ['content'],
		data() {
			return {
				introContent: {
					0: 'Investigates patterns of course offerings in the sciences and the humanities, arguing for discernible differences in approaches to teaching and learning between what C. P. Snow called the "two cultures" of academic life.',
					1: 'Examines the establishment of a popular musical genre as a topic of scholarly study, as it makes its way from controversy into the curriculum.',
					2: 'Seeks out the surprising impact of the material turn in literary studies across disciplines, from the arts and humanities to the social sciences and caring professions.',
					3: 'Tracing two famous authors though their own course choices, and the impact they have on teaching today.',
					4: 'Looks at the rise of this preeminent Harvard program, and the invisible curriculum sought out—and protested—by black students at Harvard before its founding.'
				},
				statements: {
					year: 'year',
					lineGroup: 'externalCourseId',
					keyword: 'COMPUTER_SCIENCE',
					keywordTitle: 'Computer Science',
					startYear: 1990,
					endYear: 2010,
					searchMode: "department",
					areaGroup: 'component', // subjectDescription // component // externalCourseId
					maxCall: 10000
				}
			}
		},
		components: {
			Share,
			LineStackingIntro
		},
		computed: {
            clientWidth: () => {
                return d3Select('#app').node().getBoundingClientRect().width;
            },
            clientHeight: () => {
                return d3Select('#app').node().getBoundingClientRect().height;
			},
			storiesArr() {
				return this.content.stories;
			}
		},
		methods: {
			dispatchIntroVisible(state) {
				this.$store.dispatch('SET_INTRO_VISIBLE', state);
			},
			toggleIntroVisible() {
				d3Select('.intro').classed('hide', true);
				setTimeout(() => {
					this.dispatchIntroVisible(false);
				}, 500)
			},
            dispatchCurrentStory(state) {
				d3Select('.intro').classed('hide', true);
				d3Select('.Nav').style('display', 'flex');
				setTimeout(() => {
					this.dispatchIntroVisible(false);
				}, 500)
                this.$store.dispatch("SET_CURRENT_STORY", state);
			},
			resize() {
				const height = d3Select('#app').node().getBoundingClientRect().height;
				const width = d3Select('#app').node().getBoundingClientRect().width;
			}
		},
		created() {
			d3Select('.intro').style('width', this.clientWidth);
			d3Select('.intro').style('height', this.clientHeight);
		},
		mounted() {
			window.addEventListener('resize', () => {
				this.resize()
			})
		}
	}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
	@import '../assets/style/main.scss'; 

	#headline-wrapper {
		align-self: center;
		display: flex;
		flex-direction: column;

		@media screen and (max-height: 700px) {
			span:first-of-type {
				margin-right: 10px;
			}
			flex-direction: row;
		}
	}

	.story-title {
		display: flex;
    	justify-content: space-between;

		svg {
			opacity: .25;
			transform: translate(-6px,5px);
			transition: all $time-s ease-in-out;
		}
	}

	.intro-outer {
		width: 400px;
		z-index: 100000;
		display: block;
		flex-direction: column;
		justify-content: space-around;

		@media screen and (max-width: 550px) {
			width: 285px;
		}

		.subline {
			margin-top: 40px;
			font-style: italic;
			width: 300px;
		}
	}

	.typo-wrapper {
		// top: 100px;
		display: flex;
		flex-direction: column;
		text-align: center;
		z-index: 100;
		margin-bottom: 40px;
		background: rgba(255,255,255,.6);

		@media screen and (max-height: 750px) {
			margin-bottom: 40px;
		}

		.typo-small {
			font-size: 18px;
			font-weight: 600;
			margin-top: 20px;
			margin-bottom: 10px;

			@media screen and (max-height: 750px) {
				margin-top: 5px;
			}
		}

		.typo-large {
			font-size: 54px;
			font-weight: 600;
			line-height: 100%;
			letter-spacing: -1px;

			@media screen and (max-height: 750px) {
				font-size: 32px;
			}
		}

		.typo-by {
			font-style: italic;
			font-size: 13px;
			width: 200px;
			margin: 0 auto;
			line-height: 160%;
			margin-top: 20px;

			box-shadow: white 0px 0px 50px;

			@media screen and (max-height: 750px) {
				margin-top: 5px;
			}
		}
	}

	.scroll-wrapper {
		width: 230px;
		margin: 0 auto;
		align-self: center;
		margin-top: 30px;
		display: flex;
		align-items: center;
		font-weight: 600;

		.arrow {
			font-size: 36px;
			font-weight: 500;
			margin-right: 10px;
		}

		.text {
			font-size: 16px;
		}
	}

	.intro-canvas {
		background: white;
		box-shadow: #f7f7f7 4px 4px 60px;
		border: 1px solid #dfdfdf;
		border-radius: 8px;
		overflow: scroll;
		height: 400px;
		margin-top: 40px;
		display: block;
		-ms-overflow-style: none;
		// display: flex;
		// flex-direction: column;
		// justify-content: space-around;
		// align-items: center;
		margin: 0 auto;


		@media screen and (max-width: 550px) {
			width: 295px;
		}

		@media screen and (max-height: 600px) and (max-width: 380px) {
			width: 284px;
			height: 300px;
		}
	}

	.text-wrap {
		margin-top: 40px;
		margin-left: 40px;
		width: 320px;
		margin-bottom: 40px;
		font-size: 13px;
		line-height: 165%;


		@media screen and (max-width: 550px) {
			margin-top: 10px;
			margin-left: 12.5px;
			width: 275px;
		}

		@media screen and (max-height: 600px) and (max-width: 380px) {
			width: 250px;
		}
	}

	.story-outer {
		width: 290px;
		margin-left: 40px;
		display: flex;
		flex-direction: column;
		// font-weight: 600;
		font-size: 13px;
		// border: 1px solid #e8e8e8;
		margin-bottom: 15px;
		transition: all $time-s ease-in-out;
		border: 0;
		background: rgb(249, 249, 249);
		border-radius: 4px;
		padding: 15px;
		cursor: pointer;

		@media screen and (max-width: 550px) {
			margin-top: 10px;
			margin-left: 12.5px;
			width: 239px;
		}

		@media screen and (max-height: 600px) and (max-width: 380px) {
			width: 229px;
		}

		&:hover {
			background:#f4f4f4;

			.story-title {
				svg {
					opacity: 1;
					transform: translate(-2px,5px);
					transition: all $time-s ease-in-out;
				}
			}
		}

		span {
			font-size: 18px;
			font-weight: 600;
			margin-bottom: 5px;
		}
		
		span.subtitle {
			margin-left: 0 !important;
			margin-top: 0px;
			line-height: 160%;
			opacity: .5;
			font-weight: 500;
			font-size: 13px;
		}
	}

	.text-wrapper {
		width: 300px;
		font-size: 13px;
		line-height: 160%;

		p {
			margin-top: 20px;	
		}
	}

	.intro {
		position: absolute;
		height: 100%;
		z-index: 100;
		width: 100%;
		top: 0;
		overflow: scroll;
		background: white;
		z-index: 110000;
		display: flex;
		justify-content: center;
		align-items: center;
		transition: opacity $time-s ease-in-out;

		.wrapper-intro {
			display: flex;
			justify-content: start;
			flex-direction: column;
			position: absolute;
			align-items: start;
			width: auto;
			overflow-x: hidden;
			height: auto;
			background: $color-secondary-10;
			border-radius: 8px;
			left: 0px;
			top: 10px;
			bottom: 10px;
			width: 320px;
			border: 1px solid #e4e4e4;

			@media screen and (min-width: 768px) {
				width: 520px;
			}

			.title {
				font-size: 48px;
				line-height: 95%;
				letter-spacing: -0.5px;
				margin-top: 15px;
				margin-left: 40px;
				margin-top: 34px;
				background: none !important;

				@media screen and (max-width: 768px) {
					margin-left: 10px;
				}
			}

			.subtitle {
				text-align: left;
				font-weight: 400;
				font-size: 13px;
				line-height: 140%;
				// transform: translateX(3px);
				margin-bottom: 0px;
				margin-top: 10px;
				margin-left: 40px;


				@media screen and (max-width: 768px) {
					font-size: 13px;
					margin-top: 10px;
					margin-left: 10px;
					width: 270px;
				}
			}

			.stories-intro {
				margin-bottom: 00px;
				margin-left: 40px;
				margin-top: 40px;
				line-height: 180%;
				font-size: 13px;

				@media screen and (max-width: 768px) {
					margin-left: 10px;
				}
			}

			span.subtitle {
				text-align: left;
				font-weight: normal;
				font-size: 13px;
				line-height: 160%;
			}

			.intro--text {
				font-size: 13px;
				line-height: 180%;
				font-weight: 400;
				font-style: normal;
				//opacity: 1 !important;
				margin-top: 60px;
				margin-bottom: 60px;
				width: 420px;
				margin-left: 40px;
				
				// transform: translateX(3px);

				// transition: all $time-m ease-in-out;

				@media screen and (max-width: 768px) {
					//font-size: 16px;
					width: 280px;
					margin-left: 10px;
				}

				&.visible {
					
				}
			}

			.visible {
				opacity: 1;
				// transform: translateY(-10px);
				// transition: all $time-m ease-in-out;
			}

			.btn.visible:hover {
				background: black;
			}

			.subtitle.visible {
				opacity: 1;
			}
		}

		.by {
			margin-bottom: 20px;
			margin-left: 40px;
			margin-top: 15px;
			width: 280px;
			opacity: 0;
			line-height: 180%;
			font-style: italic;
			font-size: 13px;
			transition: all $time-m ease-in-out;

			@media screen and (max-width: 768px) {

				margin-left: 10px;
			}
		}

	}

	.hide {
		opacity: 0;
		// transform: translateY(-50px);
		// transition: all $time-m ease-in-out;
	}

	.btn {
		margin-top: 20px;
		border: 1px solid $color-primary;
		padding: 7px 10px 9px 10px;
		border-radius: 4px;
		font-size: $font-size-m;
		cursor: pointer;
		transition: all $time-xs ease;

		opacity: 0;
		transform: translateY(10px);
		transition: all $time-m ease-in-out;

		width: 110px;

		&:hover {
			background: $color-primary;
			color: $color-white;
			transition: all $time-xs ease !important;
		}

		&:focus {
			outline: none;
		}
	}

	
</style>
